// ExpandedProjectView.jsx

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Form,
  Button,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaCalendarAlt,
  FaUserAlt,
  FaUsers,
  FaClock,
  FaEdit,
  FaCheckCircle,
  FaHourglassHalf,
  FaTrash,
  FaCog,
  FaWhatsapp,
  FaList,
  FaEnvelope,
  FaSignOutAlt,
} from "react-icons/fa";
import "../ExpandedProjectView.css";
import { useUser } from "../contextProviders/userContext";
import { API_BASE_URL } from "../api";
import { deduplicateReflections } from "../utils/reflections";
import ProjectFormsPanel from "./ProjectFormsPanel";
import ProjectLeaderManagementModal from "./ProjectLeaderManagementModal";
import LeaderProjectView from "../screens/student/LeaderProjectView";
import { useNavigate } from "react-router-dom";

const MAX_LOG_HOURS = 15;

const ExpandedProjectView = ({ project }) => {
  const [reflection, setReflection] = useState("");
  const [pictures, setPictures] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [participationData, setParticipationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [reflections, setReflections] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingReflectionId, setDeletingReflectionId] = useState(null);
  const [reflectionError, setReflectionError] = useState("");
  const [showLogHoursModal, setShowLogHoursModal] = useState(false);
  const [logStartTime, setLogStartTime] = useState("");
  const [logEndTime, setLogEndTime] = useState("");
  const [logReflection, setLogReflection] = useState("");
  const [logEvidence, setLogEvidence] = useState([]);
  const [logHoursError, setLogHoursError] = useState("");
  const [logHoursSuccess, setLogHoursSuccess] = useState("");
  const [isLoggingHours, setIsLoggingHours] = useState(false);
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [projectLogs, setProjectLogs] = useState([]);
  const [logsError, setLogsError] = useState(null);
  const [showEditLogModal, setShowEditLogModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editLogStartTime, setEditLogStartTime] = useState("");
  const [editLogEndTime, setEditLogEndTime] = useState("");
  const [editLogReflection, setEditLogReflection] = useState("");
  const [editLogExistingEvidence, setEditLogExistingEvidence] = useState([]);
  const [editLogRemovedEvidence, setEditLogRemovedEvidence] = useState([]);
  const [editLogNewEvidence, setEditLogNewEvidence] = useState([]);
  const [editLogError, setEditLogError] = useState("");
  const [isEditingLog, setIsEditingLog] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState(null);
  const [ownLogsError, setOwnLogsError] = useState("");
  const [isLeavingProject, setIsLeavingProject] = useState(false);
  const [leaveProjectError, setLeaveProjectError] = useState("");

  const savingRef = useRef(false);
  const submissionIdRef = useRef(null);
  const { user } = useUser();
  const navigate = useNavigate();
  const currentUserId = user?.id || user?._id;
  const isProjectLeader = Boolean(
    project?.userRole === "leader" ||
      (project?.createdBy && String(project.createdBy) === String(user?.id || user?._id))
  );
  const whatsappLinks = [
    {
      label: "WhatsApp group",
      url: project?.communicationLinks?.whatsappGroup,
    },
    {
      label: "WhatsApp channel",
      url: project?.communicationLinks?.whatsappChannel,
    },
  ].filter((link) => link.url);
  const contactableLeaders = (project?.leaders || []).filter(
    (leader) => String(leader?._id) !== String(currentUserId)
  );
  const projectReflections = deduplicateReflections(reflections);

  useEffect(() => {
    fetchParticipationData();
    if (project?.reflections) {
      setReflections(project.reflections);
    } else {
      setReflections([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchParticipationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/participations/${project._id}`,
        { headers: getAuthHeader() }
      );
      setParticipationData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching participation data:", error);
      setError("Failed to fetch participation data. Please try again later.");
      setLoading(false);
    }
  };

  const handleViewLogs = async () => {
    setLogsError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/projects/${project._id}/logs`,
        { headers: getAuthHeader() }
      );
      setProjectLogs(response.data);
      setShowLogsModal(true);
    } catch (error) {
      console.error("Error fetching project logs:", error);
      setLogsError("Failed to fetch project logs. Please try again.");
    }
  };

  const handleUpdateLog = async (logId, updates) => {
    try {
      await axios.post(
        `${API_BASE_URL}/participations/approve-hours`,
        { projectId: project._id, logId, ...updates },
        { headers: getAuthHeader() }
      );
      const response = await axios.get(
        `${API_BASE_URL}/projects/${project._id}/logs`,
        { headers: getAuthHeader() }
      );
      setProjectLogs(response.data);
    } catch (error) {
      console.error("Error updating log:", error);
      setLogsError("Failed to update log. Please try again.");
    }
  };

  const handleReflectionChange = (e) => {
    setReflection(e.target.value);
  };

  const handlePictureUpload = (e) => {
    const files = Array.from(e.target.files);
    setPictures([...pictures, ...files].slice(0, 3)); // Ensure max 3 pictures
  };

  const handleSaveReflection = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    let saved = false;

    try {
      setSaveError(null);

      // Check if user is authenticated
      const userId = user.id;
      console.log(userId);
      if (!userId) {
        console.log("User not authenticated. Please log in again.");
        setSaveError("User not authenticated. Please log in again.");
        return;
      }

      if (!reflection.trim() && pictures.length === 0) {
        setSaveError("Add a written reflection or at least one evidence image.");
        return;
      }

      // Create FormData and append all necessary fields
      const formData = new FormData();
      formData.append("reflection", reflection);
      formData.append("submissionId", submissionIdRef.current);

      // Append pictures if any
      pictures.forEach((picture, index) => {
        formData.append("pictures", picture, picture.name);
      });

      // Make the API request
      const response = await axios.post(
        `${API_BASE_URL}/projects/${project._id}/reflections`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Check if the response indicates success
      if (response.data.success) {
        saved = true;
        // Show success message
        setSuccessMessage(
          response.data.message || "Reflection added successfully"
        );

        // Add the new reflection directly to the local reflections state to trigger an instant UI rerender!
        const newReflectionObj = response.data.data;
        if (newReflectionObj) {
          setReflections((previous) =>
            previous.some(
              (item) =>
                item._id &&
                newReflectionObj._id &&
                item._id.toString() === newReflectionObj._id.toString()
            )
              ? previous
              : [newReflectionObj, ...previous]
          );
        }

        // Clear form and close modal
        setReflection("");
        setPictures([]);

        // Close modal after a delay
        setTimeout(() => {
          setShowModal(false);
          setSuccessMessage("");
          setIsSaving(false);
          savingRef.current = false;
          submissionIdRef.current = null;
        }, 2000);

        // Refresh project data to display new reflections
        fetchParticipationData();
      } else {
        setSaveError("Failed to save reflection. Please try again.");
      }
    } catch (error) {
      console.error("Error saving reflection:", error);
      setSaveError(
        error.response?.data?.error ||
          "Failed to save reflection. Please try again."
      );
    } finally {
      if (!saved) {
        setIsSaving(false);
        savingRef.current = false;
      }
    }
  };

  const handleEditClick = () => {
    submissionIdRef.current =
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setSaveError(null);
    setSuccessMessage("");
    setShowModal(true);
  };

  const handleDeleteReflection = async (reflectionId) => {
    if (!reflectionId || deletingReflectionId) return;
    const confirmed = window.confirm(
      "Delete this reflection? This action cannot be undone."
    );
    if (!confirmed) return;

    setDeletingReflectionId(reflectionId);
    setReflectionError("");
    try {
      await axios.delete(
        `${API_BASE_URL}/projects/${project._id}/reflections/${reflectionId}`,
        { headers: getAuthHeader() }
      );
      setReflections((current) =>
        current.filter(
          (item) => item._id?.toString() !== reflectionId.toString()
        )
      );
    } catch (deleteError) {
      setReflectionError(
        deleteError.response?.data?.error ||
          "Failed to delete reflection. Please try again."
      );
    } finally {
      setDeletingReflectionId(null);
    }
  };

  const handleLeaveProject = async () => {
    if (isLeavingProject) return;
    const confirmed = window.confirm(
      `Are you sure you want to leave ${project.name}? You can rejoin later from Browse Projects.`
    );
    if (!confirmed) return;

    setIsLeavingProject(true);
    setLeaveProjectError("");
    try {
      await axios.delete(`${API_BASE_URL}/participations/${project._id}/leave`, {
        headers: getAuthHeader(),
      });
      window.dispatchEvent(new Event("wcco:projects-updated"));
    } catch (leaveError) {
      setLeaveProjectError(
        leaveError.response?.data?.error ||
          "Failed to leave the project. Please try again."
      );
      setIsLeavingProject(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleImageModalClose = () => {
    setSelectedImage(null);
    setShowImageModal(false);
  };

  const openLogHoursModal = () => {
    setLogStartTime("");
    setLogEndTime("");
    setLogReflection("");
    setLogEvidence([]);
    setLogHoursError("");
    setShowLogHoursModal(true);
  };

  const handleLogEvidence = (event) => {
    const files = Array.from(event.target.files || []).slice(0, 3);
    setLogEvidence(files);
    if ((event.target.files || []).length > 3) {
      setLogHoursError("You can attach a maximum of three evidence files.");
    } else {
      setLogHoursError("");
    }
  };

  const handleLogHours = async (event) => {
    event.preventDefault();
    setLogHoursError("");

    if (!logStartTime || !logEndTime) {
      setLogHoursError("Choose both a start time and an end time.");
      return;
    }

    const start = new Date(logStartTime);
    const end = new Date(logEndTime);
    if (end <= start) {
      setLogHoursError("The end time must be later than the start time.");
      return;
    }

    if ((end - start) / (1000 * 60 * 60) > MAX_LOG_HOURS) {
      setLogHoursError(
        `A single log entry cannot exceed ${MAX_LOG_HOURS} hours. Please split longer sessions into separate entries.`
      );
      return;
    }

    if (!logEvidence.length) {
      setLogHoursError(
        "Attach at least one piece of evidence (a screenshot, photo, or signed register) to support these hours."
      );
      return;
    }

    setIsLoggingHours(true);
    try {
      const formData = new FormData();
      formData.append("projectId", project._id);
      formData.append("startTime", start.toISOString());
      formData.append("endTime", end.toISOString());
      formData.append("reflection", logReflection.trim());
      logEvidence.forEach((file) => formData.append("pictures", file, file.name));

      await axios.post(`${API_BASE_URL}/participations/log-hours`, formData, {
        headers: getAuthHeader(),
      });

      await fetchParticipationData();
      setShowLogHoursModal(false);
      setLogHoursSuccess("Your hours were submitted and are awaiting review.");
    } catch (requestError) {
      setLogHoursError(
        requestError.response?.data?.error ||
          "We could not submit these hours. Please try again."
      );
    } finally {
      setIsLoggingHours(false);
    }
  };

  const toDatetimeLocalValue = (value) => {
    const date = new Date(value);
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  const openEditLogModal = (log) => {
    setEditingLog(log);
    setEditLogStartTime(toDatetimeLocalValue(log.startTime));
    setEditLogEndTime(toDatetimeLocalValue(log.endTime));
    setEditLogReflection(log.reflection || "");
    setEditLogExistingEvidence(log.evidence || []);
    setEditLogRemovedEvidence([]);
    setEditLogNewEvidence([]);
    setEditLogError("");
    setOwnLogsError("");
    setShowEditLogModal(true);
  };

  const handleEditLogNewEvidence = (event) => {
    const remainingSlots =
      3 - (editLogExistingEvidence.length + editLogNewEvidence.length);
    const files = Array.from(event.target.files || []).slice(
      0,
      Math.max(remainingSlots, 0)
    );
    if ((event.target.files || []).length > files.length) {
      setEditLogError("A log can have a maximum of 3 evidence files.");
    } else {
      setEditLogError("");
    }
    setEditLogNewEvidence((current) => [...current, ...files]);
    event.target.value = "";
  };

  const handleRemoveExistingEvidence = (url) => {
    setEditLogExistingEvidence((current) => current.filter((item) => item !== url));
    setEditLogRemovedEvidence((current) => [...current, url]);
  };

  const handleRemoveNewEvidence = (index) => {
    setEditLogNewEvidence((current) => current.filter((_, i) => i !== index));
  };

  const handleUpdateLogSubmit = async (event) => {
    event.preventDefault();
    if (!editingLog) return;
    setEditLogError("");

    if (!editLogStartTime || !editLogEndTime) {
      setEditLogError("Choose both a start time and an end time.");
      return;
    }
    const start = new Date(editLogStartTime);
    const end = new Date(editLogEndTime);
    if (end <= start) {
      setEditLogError("The end time must be later than the start time.");
      return;
    }

    if ((end - start) / (1000 * 60 * 60) > MAX_LOG_HOURS) {
      setEditLogError(
        `A single log entry cannot exceed ${MAX_LOG_HOURS} hours. Please split longer sessions into separate entries.`
      );
      return;
    }

    if (editLogExistingEvidence.length + editLogNewEvidence.length === 0) {
      setEditLogError(
        "A log must have at least one piece of evidence. Attach a screenshot, photo, or signed register."
      );
      return;
    }

    setIsEditingLog(true);
    try {
      const formData = new FormData();
      formData.append("startTime", start.toISOString());
      formData.append("endTime", end.toISOString());
      formData.append("reflection", editLogReflection.trim());
      if (editLogRemovedEvidence.length) {
        formData.append("removeEvidence", JSON.stringify(editLogRemovedEvidence));
      }
      editLogNewEvidence.forEach((file) => formData.append("pictures", file, file.name));

      await axios.put(
        `${API_BASE_URL}/participations/update-log/${editingLog._id}`,
        formData,
        { headers: getAuthHeader() }
      );

      await fetchParticipationData();
      setShowEditLogModal(false);
      setEditingLog(null);
    } catch (requestError) {
      setEditLogError(
        requestError.response?.data?.message ||
          "We could not update this log. Please try again."
      );
    } finally {
      setIsEditingLog(false);
    }
  };

  const handleDeleteOwnLog = async (logId) => {
    if (!logId || deletingLogId) return;
    const confirmed = window.confirm(
      "Delete this logged entry? This action cannot be undone."
    );
    if (!confirmed) return;

    setDeletingLogId(logId);
    setOwnLogsError("");
    try {
      await axios.delete(`${API_BASE_URL}/participations/update-log/${logId}`, {
        headers: getAuthHeader(),
      });
      await fetchParticipationData();
    } catch (deleteError) {
      setOwnLogsError(
        deleteError.response?.data?.message ||
          "Failed to delete this log. Please try again."
      );
    } finally {
      setDeletingLogId(null);
    }
  };

  if (loading) {
    return (
      <div className="expanded-project-loading" role="status">
        <Spinner animation="border" size="sm" />
        <span>Loading project activity…</span>
      </div>
    );
  }

  if (error) {
    return <Alert>{error}</Alert>;
  }

  const logs = participationData?.logs || [];
  const approvedHours = logs.reduce(
    (sum, log) => sum + (log.status === "approved" ? log.hours : 0),
    0
  );

  const pendingHours = logs.reduce(
    (sum, log) => sum + (log.status === "pending" ? log.hours : 0),
    0
  );

  const totalHours = logs.reduce((sum, log) => sum + (Number(log.hours) || 0), 0);
  const formatDate = (value) =>
    value
      ? new Intl.DateTimeFormat("en-ZA", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(value))
      : "Ongoing";
  const formatDateTime = (value) =>
    new Intl.DateTimeFormat("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));

  return (
    <div className="expanded-project-view">
      <section className="project-overview" aria-labelledby="selected-project-title">
        <div className="project-overview__header">
          <div>
            <p className="project-overview__eyebrow">Selected project</p>
            <h1 id="selected-project-title">{project.name}</h1>
          </div>
          <div className="project-overview__actions">
            {whatsappLinks.map((link) => (
              <a
                key={link.label}
                className="project-overview__whatsapp-action"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${project.name} ${link.label}`}
              >
                <FaWhatsapp aria-hidden="true" />
                {link.label}
              </a>
            ))}
            {isProjectLeader && (
              <>
                <Button type="button" variant="outline-primary" onClick={handleViewLogs}>
                  <FaList aria-hidden="true" />
                  Review hours
                </Button>
                <Button type="button" variant="outline-primary" onClick={() => setShowManagementModal(true)}>
                  <FaCog aria-hidden="true" />
                  Manage project
                </Button>
              </>
            )}
            <Button type="button" className="project-log-hours-action desktop-log-hours" onClick={openLogHoursModal}>
              <FaClock aria-hidden="true" />
              Log hours
            </Button>
            {!isProjectLeader && (
              <Button
                type="button"
                variant="outline-danger"
                onClick={handleLeaveProject}
                disabled={isLeavingProject}
              >
                {isLeavingProject ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FaSignOutAlt aria-hidden="true" />
                )}
                Leave project
              </Button>
            )}
          </div>
        </div>
        {logHoursSuccess && (
          <Alert dismissible onClose={() => setLogHoursSuccess("")} className="mt-3">
            {logHoursSuccess}
          </Alert>
        )}
        {leaveProjectError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setLeaveProjectError("")}
            className="mt-3"
          >
            {leaveProjectError}
          </Alert>
        )}
        <p className="project-overview__description">
          {project.description || "No project description has been provided."}
        </p>

        <div className="project-metadata">
          <div><FaCalendarAlt aria-hidden="true" /><span><small>Programme dates</small>{formatDate(project.startDate)} – {formatDate(project.endDate)}</span></div>
          <div className="project-metadata-organiser-row">
            <div><FaUserAlt aria-hidden="true" /><span><small>Organiser</small>{project.organiserName} · {project.organiserType}</span></div>
            <Button type="button" className="project-log-hours-action mobile-log-hours" onClick={openLogHoursModal}>
              <FaClock aria-hidden="true" />
              Log hours
            </Button>
          </div>
        </div>

        {!isProjectLeader && (
          <div className="project-leader-contacts">
            <div className="project-leader-contacts__heading">
              <div>
                <strong>Project leaders</strong>
                <span>Inbox a leader if you need help with this project.</span>
              </div>
              <Button
                type="button"
                variant="link"
                onClick={() => navigate("/messages")}
              >
                View administrators
              </Button>
            </div>
            {contactableLeaders.length ? (
              <div className="project-leader-contacts__list">
                {contactableLeaders.map((leader) => {
                  const name = leader.organizationName || `${leader.name || "Project"} ${leader.surname || "leader"}`.trim();
                  return (
                    <div key={leader._id} className="project-leader-contact">
                      <span><FaUserAlt aria-hidden="true" /><strong>{name}</strong></span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-primary"
                        onClick={() => navigate(`/messages?userId=${leader._id}`)}
                        aria-label={`Message ${name}`}
                      >
                        <FaEnvelope aria-hidden="true" /> Message
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="project-leader-contacts__empty">No project leader is currently listed. Contact an administrator from Messages.</p>
            )}
          </div>
        )}

        <div className="project-impact-grid">
          <div><FaCheckCircle aria-hidden="true" /><span>Approved</span><strong>{approvedHours.toFixed(1)} hrs</strong></div>
          <div><FaHourglassHalf aria-hidden="true" /><span>Pending</span><strong>{pendingHours.toFixed(1)} hrs</strong></div>
          <div><FaClock aria-hidden="true" /><span>All logged</span><strong>{totalHours.toFixed(1)} hrs</strong></div>
          <div><FaUsers aria-hidden="true" /><span>Participants</span><strong>{project.participants?.length || 0}</strong></div>
        </div>
      </section>

      <ProjectFormsPanel projectId={project._id} visible />

      <section className="project-workspace-section" aria-labelledby="logged-hours-title">
        <div className="project-section-header">
          <div>
            <h2 id="logged-hours-title">Logged hours</h2>
            <p>Your submitted activity and current review status.</p>
          </div>
          <span className="wcco-status">{logs.length} record{logs.length === 1 ? "" : "s"}</span>
        </div>
        {ownLogsError && (
          <Alert variant="danger" dismissible onClose={() => setOwnLogsError("")}>
            {ownLogsError}
          </Alert>
        )}
        {logs.length ? (
          <div className="project-log-table-wrap">
            <table className="project-log-table">
              <thead>
                <tr><th>Start</th><th>End</th><th>Hours</th><th>Reflection & evidence</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {logs.map((log, index) => {
                  const canModify = log.status !== "approved";
                  return (
                  <tr key={log._id || `${log.startTime}-${index}`}>
                    <td data-label="Start">{formatDateTime(log.startTime)}</td>
                    <td data-label="End">{formatDateTime(log.endTime)}</td>
                    <td data-label="Hours"><strong>{Number(log.hours || 0).toFixed(1)} hrs</strong></td>
                    <td data-label="Reflection & evidence">
                      {log.reflection && <p className="project-log-reflection">{log.reflection}</p>}
                      {log.evidence?.map((url, evidenceIndex) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer">Evidence {evidenceIndex + 1}</a>
                      ))}
                      {!log.reflection && !log.evidence?.length && <span>None supplied</span>}
                    </td>
                    <td data-label="Status"><span className={`project-log-status status-cell-${log.status}`}>{log.status}</span></td>
                    <td data-label="Actions">
                      {canModify ? (
                        <div className="project-log-actions">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            aria-label="Edit log"
                            title="Edit log"
                            onClick={() => openEditLogModal(log)}
                          >
                            <FaEdit size={12} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            aria-label="Delete log"
                            title="Delete log"
                            disabled={deletingLogId === log._id}
                            onClick={() => handleDeleteOwnLog(log._id)}
                          >
                            {deletingLogId === log._id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FaTrash size={12} />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="project-log-actions-locked">Locked</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="project-section-empty">
            <p>No hours have been logged for this project.</p>
            <Button type="button" variant="outline-primary" onClick={openLogHoursModal}>
              <FaClock aria-hidden="true" /> Log your first hours
            </Button>
          </div>
        )}
      </section>

      <section className="project-workspace-section" aria-labelledby="reflections-title">
        <div className="project-section-header project-section-header--action">
          <div>
            <h2 id="reflections-title">Project reflections</h2>
            <p>Shared learning and supporting evidence from project members.</p>
          </div>
          <Button onClick={handleEditClick} size="sm"><FaEdit size={13} /> Add reflection</Button>
        </div>
        {reflectionError && <Alert>{reflectionError}</Alert>}
        {projectReflections.length > 0 ? (
          <div className="reflection-list">
            {projectReflections.map((reflection, index) => {
              const creatorId = reflection.createdBy?._id || reflection.createdBy;
              const currentUserId = user?.id || user?._id;
              const canDelete =
                user?.role === "admin" ||
                (creatorId && currentUserId && creatorId.toString() === currentUserId.toString());
              const authorName = reflection.createdBy?.name
                ? `${reflection.createdBy.name} ${reflection.createdBy.surname || ""}`.trim()
                : "Project member";
              return (
              <article key={reflection._id || `${reflection.createdAt}-${index}`} className="reflection-card">
                <div className="reflection-card__header">
                  <div>
                    <strong>{authorName}</strong>
                    <time dateTime={reflection.createdAt}>{formatDateTime(reflection.createdAt)}</time>
                  </div>
                  {canDelete && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      aria-label="Delete reflection"
                      title="Delete reflection"
                      disabled={deletingReflectionId === reflection._id}
                      onClick={() => handleDeleteReflection(reflection._id)}
                    >
                      {deletingReflectionId === reflection._id ? <Spinner animation="border" size="sm" /> : <FaTrash size={12} />}
                    </Button>
                  )}
                </div>
                {reflection.text && <p className="reflection-card__text">{reflection.text}</p>}
                {reflection.pictures && reflection.pictures.length > 0 && (
                  <div className="reflection-gallery">
                    {reflection.pictures.map((picture, picIndex) => (
                      <button key={picture || picIndex} type="button" onClick={() => handleImageClick(picture)}>
                        <img
                          src={picture}
                          alt={`Evidence ${picIndex + 1} for reflection ${index + 1}`}
                          className="clickable-image"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </article>
              );
            })}
          </div>
        ) : (
          <div className="project-section-empty">No project reflections have been shared yet.</div>
        )}
      </section>

      <Modal show={showImageModal} onHide={handleImageModalClose} centered size="lg">
        <Modal.Body>
          {selectedImage && (
            <img src={selectedImage} alt="Selected reflection evidence" className="reflection-modal-image" />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showLogHoursModal}
        onHide={() => !isLoggingHours && setShowLogHoursModal(false)}
        centered
      >
        <Form onSubmit={handleLogHours}>
          <Modal.Header closeButton>
            <Modal.Title>Log volunteer hours</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="log-hours-project-name">{project.name}</p>
            <p className="log-hours-guidance">
              Enter the time you volunteered. A project leader or administrator will review the submission.
            </p>
            {logHoursError && <Alert>{logHoursError}</Alert>}

            <div className="log-hours-time-grid">
              <Form.Group>
                <Form.Label>Start time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={logStartTime}
                  max={logEndTime || undefined}
                  onChange={(event) => setLogStartTime(event.target.value)}
                  disabled={isLoggingHours}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>End time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={logEndTime}
                  min={logStartTime || undefined}
                  onChange={(event) => setLogEndTime(event.target.value)}
                  disabled={isLoggingHours}
                  required
                />
              </Form.Group>
            </div>

            <Form.Group className="mt-3">
              <Form.Label>Activity summary <span>(optional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={5000}
                value={logReflection}
                onChange={(event) => setLogReflection(event.target.value)}
                placeholder="Briefly describe what you did"
                disabled={isLoggingHours}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Supporting evidence <span>(required, maximum 3 files)</span></Form.Label>
              <Form.Text className="d-block mb-2">
                Attach a screenshot, photo, or a signed register/attendance form as proof of these hours.
              </Form.Text>
              <Form.Control
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,application/pdf"
                onChange={handleLogEvidence}
                disabled={isLoggingHours}
                required
              />
              {logEvidence.length > 0 && (
                <ul className="log-hours-file-list">
                  {logEvidence.map((file) => <li key={`${file.name}-${file.lastModified}`}>{file.name}</li>)}
                </ul>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => setShowLogHoursModal(false)}
              disabled={isLoggingHours}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoggingHours}>
              {isLoggingHours ? <><Spinner animation="border" size="sm" /> Submitting…</> : "Submit hours"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showEditLogModal}
        onHide={() => !isEditingLog && setShowEditLogModal(false)}
        centered
      >
        <Form onSubmit={handleUpdateLogSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit logged hours</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="log-hours-guidance">
              Editing this entry will reset its status to pending for re-review.
            </p>
            {editLogError && <Alert variant="danger">{editLogError}</Alert>}

            <div className="log-hours-time-grid">
              <Form.Group>
                <Form.Label>Start time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={editLogStartTime}
                  max={editLogEndTime || undefined}
                  onChange={(event) => setEditLogStartTime(event.target.value)}
                  disabled={isEditingLog}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>End time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={editLogEndTime}
                  min={editLogStartTime || undefined}
                  onChange={(event) => setEditLogEndTime(event.target.value)}
                  disabled={isEditingLog}
                  required
                />
              </Form.Group>
            </div>

            <Form.Group className="mt-3">
              <Form.Label>Activity summary <span>(optional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={5000}
                value={editLogReflection}
                onChange={(event) => setEditLogReflection(event.target.value)}
                placeholder="Briefly describe what you did"
                disabled={isEditingLog}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Supporting evidence <span>(required, maximum 3 files)</span></Form.Label>
              <Form.Text className="d-block mb-2">
                Attach a screenshot, photo, or a signed register/attendance form as proof of these hours.
              </Form.Text>
              {editLogExistingEvidence.length > 0 && (
                <ul className="log-hours-file-list">
                  {editLogExistingEvidence.map((url) => (
                    <li key={url}>
                      <a href={url} target="_blank" rel="noreferrer">
                        {url.split("/").pop()}
                      </a>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="log-evidence-remove"
                        onClick={() => handleRemoveExistingEvidence(url)}
                        disabled={
                          isEditingLog ||
                          editLogExistingEvidence.length + editLogNewEvidence.length <= 1
                        }
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              {editLogExistingEvidence.length + editLogNewEvidence.length < 3 && (
                <Form.Control
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/gif,application/pdf"
                  onChange={handleEditLogNewEvidence}
                  disabled={isEditingLog}
                />
              )}
              {editLogNewEvidence.length > 0 && (
                <ul className="log-hours-file-list">
                  {editLogNewEvidence.map((file, index) => (
                    <li key={`${file.name}-${file.lastModified}`}>
                      {file.name}
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="log-evidence-remove"
                        onClick={() => handleRemoveNewEvidence(index)}
                        disabled={isEditingLog}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => setShowEditLogModal(false)}
              disabled={isEditingLog}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isEditingLog}>
              {isEditingLog ? <><Spinner animation="border" size="sm" /> Saving…</> : "Save changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showModal} onHide={() => !isSaving && setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add reflection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger">{saveError}</Alert>}

          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Your reflection (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reflection}
                onChange={handleReflectionChange}
                placeholder="What did you learn or contribute?"
                disabled={isSaving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Evidence pictures (optional, maximum 3)</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={handlePictureUpload}
                disabled={pictures.length >= 3}
              />
              <div className="reflection-upload-preview">
                {pictures.map((picture, index) => (
                  <img
                    key={index}
                    src={URL.createObjectURL(picture)}
                    alt={`Preview ${index + 1}`}
                    className="img-thumbnail-preview"
                  />
                ))}
              </div>
            </Form.Group>
            <Button
              variant="primary"
              onClick={handleSaveReflection}
              disabled={isSaving}
              className="w-100">
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : "Save Reflection"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        size="xl"
        centered>
        <Modal.Header closeButton>
          <Modal.Title>Logs for {project.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logsError && <Alert variant="danger">{logsError}</Alert>}
          <div style={{ overflowX: "auto" }}>
            <LeaderProjectView
              projectLogs={projectLogs}
              onUpdateLog={handleUpdateLog}
              isProjectLeader={isProjectLeader}
            />
          </div>
        </Modal.Body>
      </Modal>

      {isProjectLeader && (
        <ProjectLeaderManagementModal
          show={showManagementModal}
          onHide={() => setShowManagementModal(false)}
          project={project}
          onUpdated={() => window.dispatchEvent(new Event("wcco:projects-updated"))}
        />
      )}
    </div>
  );
};

export default ExpandedProjectView;
